import { AutoShell as AppShell } from "@/components/Nav";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";

const LAST_UPDATED = "February 10, 2026";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      "By creating an account, accessing, or using Aura Credit (\"Aura,\" \"we,\" \"our,\" \"us\"), you (\"User,\" \"you,\" \"your\") agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these Terms, do not use the service.",
      "You must be at least 18 years old and a U.S. resident to use Aura. By using the service, you represent that you meet these requirements.",
    ],
  },
  {
    id: "nature-of-service",
    title: "2. Nature of the Service",
    body: [
      "Aura is a self-service software tool that provides: AI-drafted dispute letter templates, automated dispatch of USPS Certified Mail via third-party providers (Lob), an educational credit-building playbook, an admin-managed marketplace for authorized-user tradelines, and payment processing via Stripe.",
      "Aura is NOT: a law firm, a credit-repair organization operating on your behalf under CROA, a bank, a credit bureau, a credit-reporting agency, a debt collector, a financial advisor, or an insurance provider. Nothing on Aura constitutes legal, financial, or tax advice.",
      "You retain full and exclusive control over every dispute, letter, tradeline order, and communication. Aura provides the software; you make the decisions.",
    ],
  },
  {
    id: "eligibility",
    title: "3. Account Eligibility & Security",
    body: [
      "You are responsible for maintaining the confidentiality of your login credentials. You agree to notify us immediately of any unauthorized access. Aura is not liable for losses resulting from unauthorized use of your account caused by your failure to safeguard credentials.",
      "You may not use Aura to dispute items you know to be accurate, to file frivolous disputes, or to commit fraud against creditors or bureaus. Doing so may violate federal law and will result in immediate termination of your account.",
    ],
  },
  {
    id: "no-guarantees",
    title: "4. No Guarantees of Outcomes",
    body: [
      "Aura makes no representations or warranties about specific credit score changes, item removals, loan approvals, tradeline posting timing, USPS delivery timing, or bureau response times. Credit reporting outcomes depend on many factors outside our control, including but not limited to: the accuracy of information you provide, bureau investigation policies, furnisher responses, USPS operational conditions, and applicable scoring model.",
      "You acknowledge that AI-generated letters and content may contain errors, may reference laws imperfectly, and may not fit every specific situation. You agree to review every letter and communication before it is sent.",
    ],
  },
  {
    id: "croa",
    title: "5. Credit Repair Organizations Act (CROA) Disclosures",
    body: [
      "Under the CROA (15 U.S.C. §1679 et seq.), you have the following rights:",
      "• You have the right to dispute inaccurate information in your credit report directly with the credit bureaus, for free, at any time. You do not need a credit-repair company to do this.",
      "• You have the right to receive a copy of your credit report from each major bureau for free every year at annualcreditreport.com.",
      "• You have the right to sue a credit repair organization that violates the Credit Repair Organizations Act.",
      "• You have the right to cancel your contract with any credit repair organization for any reason within three business days from the date you signed it.",
      "• Credit bureaus are required to follow reasonable procedures to assure accurate reporting. If bureaus fail to do so, you may sue them.",
      "Aura believes it operates as a self-service software tool rather than a credit-repair organization under CROA, because Users direct all activity themselves and Aura performs no work on Users' behalf that Users could not perform themselves. Nevertheless, we include these disclosures to protect your rights and inform you fully.",
    ],
  },
  {
    id: "fees",
    title: "6. Subscriptions, Fees & Refunds",
    body: [
      "Aura offers three tiers: Free (limited), Basic ($39.99/month, includes 5 certified mail sends), Plus ($77.77/month, unlimited certified mail + advanced tools). Prices are subject to change with 30 days' notice; existing subscribers keep their locked-in rate for the remainder of their current billing period.",
      "Certified-mail packs ($79 for 10 letters) are one-time purchases and non-refundable once letters begin dispatching.",
      "Tradeline purchases are refunded if the tradeline fails to post to at least one of the three major bureaus within 45 days of purchase. Refund requests must be submitted in writing to support@aura-credit.app.",
      "Monthly subscriptions may be cancelled at any time. Cancellations take effect at the end of the current billing period. No pro-rated refunds are issued for partial months except where required by law.",
      "You authorize Aura to charge your payment method on file for all fees. Failed payments may result in suspension of paid features.",
    ],
  },
  {
    id: "third-parties",
    title: "7. Third-Party Services",
    body: [
      "Aura relies on: Anthropic Claude for AI text generation, Lob for USPS Certified Mail dispatch, Stripe for payment processing, MongoDB Atlas for data storage, and web push providers for notifications. Each has its own terms and privacy policy. Your use of Aura constitutes agreement that data necessary to complete tasks you request may be shared with these providers.",
      "We may add, remove, or change third-party providers at any time to maintain or improve service quality.",
    ],
  },
  {
    id: "user-content",
    title: "8. User-Provided Content",
    body: [
      "You retain ownership of any credit report, personal information, and other content you upload to Aura. You grant us a limited, revocable, non-exclusive license to process this content solely for the purpose of providing the service you requested (parsing your report, drafting a dispute, mailing a letter, etc.).",
      "We do not use your data to train AI models. We do not sell your data. We do not use your data for advertising. See our Privacy & Disclaimer page for full detail.",
    ],
  },
  {
    id: "prohibited",
    title: "9. Prohibited Uses",
    body: [
      "You agree NOT to: (a) submit disputes for information you know to be accurate; (b) impersonate any person or entity; (c) upload malicious code or attempt to breach Aura's security; (d) use the service to harass creditors or bureaus; (e) resell or sublicense Aura's services without written permission; (f) use the service in a jurisdiction where it is unlawful; (g) circumvent quota, rate-limit, or payment mechanisms; (h) scrape, mass-download, or automate the service without written permission.",
    ],
  },
  {
    id: "liability",
    title: "10. Limitation of Liability",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW: (a) Aura is provided \"AS IS\" and \"AS AVAILABLE\" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement; (b) Aura's total aggregate liability to you for any and all claims arising from or relating to the service is capped at the greater of (i) $100 or (ii) the total fees paid by you to Aura in the twelve (12) months preceding the claim; (c) Aura is not liable for indirect, incidental, consequential, special, exemplary, or punitive damages, including lost profits, lost data, loss of goodwill, or business interruption, even if advised of the possibility.",
      "Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability, so the above may not apply to you in full.",
    ],
  },
  {
    id: "indemnity",
    title: "11. Indemnification",
    body: [
      "You agree to indemnify, defend, and hold harmless Aura, its officers, employees, contractors, and third-party providers from any claim, demand, damage, or expense (including reasonable attorney's fees) arising from: (a) your use of the service, (b) your violation of these Terms, (c) your submission of inaccurate or fraudulent information, or (d) your infringement of any third party's rights.",
    ],
  },
  {
    id: "termination",
    title: "12. Termination",
    body: [
      "You may terminate your account at any time from your Billing page. Upon termination, your data is retained for 30 days for account-recovery purposes, after which it is permanently deleted (except where retention is required by law).",
      "We may suspend or terminate your account, with or without notice, if we reasonably believe you have violated these Terms or engaged in fraudulent, illegal, or abusive activity.",
    ],
  },
  {
    id: "changes",
    title: "13. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. Material changes will be communicated by email or in-app notice at least 14 days before taking effect. Continued use of the service after changes become effective constitutes acceptance of the revised Terms.",
    ],
  },
  {
    id: "law",
    title: "14. Governing Law & Dispute Resolution",
    body: [
      "These Terms are governed by the laws of the State of Delaware, USA, without regard to conflicts-of-laws principles. Any dispute arising from these Terms or the service will be resolved through binding individual arbitration under the rules of the American Arbitration Association (AAA), except where you have the right to bring a claim in small-claims court.",
      "You and Aura waive any right to a jury trial or class action, to the extent permitted by law.",
    ],
  },
  {
    id: "contact",
    title: "15. Contact",
    body: [
      "Questions about these Terms? Email support@aura-credit.app or write to Aura Credit, Attn: Legal, [company mailing address]. We respond to legitimate inquiries within five (5) business days.",
    ],
  },
];

export default function Terms() {
  return (
    <AppShell>
      <SEO
        title="Terms of Service"
        description="Aura Credit's Terms of Service. Self-service tool for FCRA §1681i disputes. CROA disclosures, refund policy, arbitration, no legal advice."
        path="/terms"
        keywords="Aura Credit terms, credit repair terms of service, CROA disclosure"
      />
      <div className="mb-8">
        <div className="overline mb-3 text-[#D5FF3F]">Legal</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">Terms of Service.</h1>
        <div className="text-xs font-mono uppercase tracking-widest text-[#6D716C] mt-3">
          Last updated · {LAST_UPDATED}
        </div>
      </div>

      {/* Table of contents */}
      <div className="card-surface p-5 mb-8" data-testid="terms-toc">
        <div className="overline mb-3">Contents</div>
        <div className="grid md:grid-cols-2 gap-y-1.5 gap-x-4">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="text-sm text-[#A8ADA5] hover:text-[#D5FF3F] transition-colors"
            >
              {s.title}
            </a>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-8 mb-12" data-testid="terms-content">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-8">
            <h2 className="font-display text-2xl md:text-3xl mb-4 leading-tight">{s.title}</h2>
            <div className="space-y-3">
              {s.body.map((p, i) => (
                <p key={i} className="text-sm text-[#A8ADA5] leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="card-surface p-6 border-[#D5FF3F]/20">
        <div className="text-sm text-[#F5F3EE] mb-2 font-medium">One more time, in one line:</div>
        <div className="text-sm text-[#A8ADA5] leading-relaxed">
          Aura is software you operate yourself. We help you draft, mail, and track — you make every
          decision. No guarantees, no legal advice, no data ever sold, and you can cancel any time.
          Full privacy and machine-only data handling detail on our{" "}
          <Link to="/app/disclaimer" className="text-[#D5FF3F] underline">
            Privacy &amp; Disclaimer page
          </Link>
          .
        </div>
      </div>
    </AppShell>
  );
}
